/**
 * Get the return to shopping URL based on the previous page or default to the homepage.
 * @param {object} params - The parameters object.
 * @param {string} params.appOrigin - The origin of the application (e.g., 'https://www.garmin.com').
 * @param {string} params.locale - The locale string (e.g., 'en-US').
 * @param {Array<string>} params.pids - Array of product IDs to include in the URL.
 * @returns {string} - The return to shopping URL.
 */
export const getReturnToShoppingUrl = (params) => {
  const { appOrigin, locale, pids } = params;

  // Used to route users to homepage if no previous category page found
  const originMap = {
    'www.garmin.com': 'https://www.garmin.com',
    'wwwpub.garmin.com': 'https://wwwpub.garmin.com',
    'www.dev.garmin.com': 'https://www.dev.garmin.com',
    'localhost.garmin.com': 'https://www.dev.garmin.com',
    'product-compare.pre.dev': 'https://home.pre.test.cv1.www.garmin.com',
    'product-compare.pre.prod.cv1': 'https://home.pre.prod.cv1.www.garmin.com',
    'product-compare.pre.prod.kc3': 'https://home.pre.prod.kc3.www.garmin.com',
  };

  // If origin includes keys from originMap, use the corresponding value; otherwise, use 'https://www.garmin.com'
  const getOrigin = (origin) => Object.entries(originMap)
    .find(([originKey]) => origin.includes(originKey))?.[1] || 'https://www.garmin.com';

  const defaultUrl = `${getOrigin(appOrigin)}/${locale}/`;

  /**
   * Matches the following
   * https://www.garmin.com/en-US/c/
   * https://category-pages.pre.prod.kc3.garmin.com/en-US/c/
   * https://category-pages.pre.prod.cv1.garmin.com/en-US/c/
   * https://wwwpub.garmin.com/en-US/c/
   * https://www.dev.garmin.com/en-US/c/
   * https://category-pages.pre.dev.cv1.garmin.com/en-US/c/
   * https://localhost.garmin.com:6021/en-US/c/
   */
  const categoryPageRegex = /^https:\/\/.+\.garmin\.com(\/|:\d{4}\/)[a-z]{2}-[A-Z]{2}\/c\//;
  const isValidCategoryUrl = (url) => categoryPageRegex.test(url);

  const { referrer } = document;

  try {
    if (referrer) {
      const referrerUrl = new URL(referrer);
      // If referrer matches category-pages URL, store it in localStorage
      if (isValidCategoryUrl(referrerUrl.toString())) {
        localStorage.setItem('previousPage', referrerUrl.toString());
      }
    }
    const previousPage = localStorage.getItem('previousPage');

    // If previousPage exists, return it with updated pids
    if (previousPage && isValidCategoryUrl(previousPage)) {
      const url = new URL(previousPage);
      url.searchParams.delete('compareProduct');
      pids.forEach((pid) => url.searchParams.append('compareProduct', pid));
      return url.toString();
    }

    return defaultUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getReturnToShoppingUrl: failed to compute return URL', {
      error,
      referrer,
    });
    return defaultUrl;
  }
};

export default getReturnToShoppingUrl;
